import { useMemo, useRef } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

/**
 * EmailBodyEditor — Quill WYSIWYG editor for email templates.
 * Hides raw HTML from the user. Exposes a "Insert variable" dropdown
 * that injects {{var}} at the current cursor position.
 */
export default function EmailBodyEditor({ value, onChange, variables = [], placeholder = "", testId }) {
  const quillRef = useRef(null);

  const modules = useMemo(() => ({
    toolbar: [
      [{ header: [false, 2, 3] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
    clipboard: { matchVisual: false },
  }), []);

  const formats = ["header", "bold", "italic", "underline", "list", "link"];

  const insertVar = (v) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection(true);
    const idx = range ? range.index : editor.getLength();
    editor.insertText(idx, v, "user");
    editor.setSelection(idx + v.length, 0, "user");
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      {variables.length > 0 && (
        <div className="flex items-center flex-wrap gap-1.5 bg-[#F0FDFA] border border-[#A7F3D0] rounded-lg p-2">
          <span className="text-[10px] uppercase font-bold tracking-widest text-[#065F46] mr-1">Insérer une variable :</span>
          {variables.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => insertVar(v)}
              data-testid={`email-var-insert-${v.replace(/[{}]/g, "")}`}
              className="text-[11px] font-mono font-bold px-2 py-0.5 rounded-md bg-white border border-[#06B6D4]/40 text-[#06B6D4] hover:bg-[#06B6D4] hover:text-white transition-colors"
            >
              {v}
            </button>
          ))}
        </div>
      )}
      <div className="rounded-lg border border-input bg-white overflow-hidden email-body-editor">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ""}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
        />
      </div>
      <style>{`
        .email-body-editor .ql-toolbar { border: 0 !important; border-bottom: 1px solid #e5e7eb !important; background: #FAFAFA; }
        .email-body-editor .ql-container { border: 0 !important; min-height: 160px; font-family: 'Inter', sans-serif; font-size: 14px; }
        .email-body-editor .ql-editor { min-height: 160px; }
        .email-body-editor .ql-editor.ql-blank::before { font-style: normal; color: #94a3b8; }
      `}</style>
    </div>
  );
}

/**
 * Parse a "{{a}} {{b}} {{c}}" string into ["{{a}}", "{{b}}", "{{c}}"].
 */
export function parseVariableTokens(spec) {
  if (!spec) return [];
  const out = spec.match(/\{\{\s*[a-zA-Z0-9_]+\s*\}\}/g) || [];
  return Array.from(new Set(out.map((t) => t.replace(/\s+/g, ""))));
}
